import { requiresApproval } from '../../_helpers/payment.guard';
import { Component, Inject, OnInit, PLATFORM_ID, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroupName,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../_services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '../../_services/error-handler.service';

@Component({
  selector: 'app-otpverification',
  imports: [RouterModule, FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './otpverification.component.html',
  styleUrl: './otpverification.component.scss',
})
export class OtpverificationComponent implements OnInit {
  otpVerificationForm: any;
  isLoading: boolean = false;
  errorMessage: string = '';
  private _snackBar = inject(MatSnackBar);
  userData: any;
  sendOtpIdentifiers: any;
  countdown: number = 20;
  showResend: boolean = false;
  interval: any;
  otpInputs: HTMLInputElement[] = [];
  constructor(
    private _fb: FormBuilder,
    private _AS: AuthService,
    private router: Router,
    private errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}
  ngOnInit() {
    this.formControls(); // build the form on both server AND client, always

    if (isPlatformBrowser(this.platformId)) {
      this.userData = history.state;
      const identi = this.userData?.isIndia ? this.userData.phone : this.userData.email;
      this.sendOtpIdentifiers = this.maskData(identi);
      this.startCountdown();
    }
  }
  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.otpInputs = Array.from(
          document.querySelectorAll('.otp-box')
        ) as HTMLInputElement[];
      });
    }
  }

  changeIdentifier(): void {
    const returnUrl = this.userData?.authReturnUrl;
    // Only return to a known authentication form, including its query params.
    const path = typeof returnUrl === 'string' ? returnUrl.split(/[?#]/)[0] : '';
    this.router.navigateByUrl(
      path === '/login' || path === '/signup' ? returnUrl : '/login',
      { replaceUrl: true }
    );
  }

  maskData(input: string): string {
    if (!input) return '';
    const isEmail = input.includes('@');
    if (isEmail) {
      // Email Mask
      const [name, domain] = input.split('@');
      const visible = name.slice(0, 3);
      const stars = '*'.repeat(Math.max(3, name.length - 3));
      return `${visible}${stars}@${domain}`;
    } else {
      // Phone Mask
      const num = input;
      const last4 = num.slice(-4);
      return `******${last4}`;
    }
  }

  formControls() {
    this.otpVerificationForm = this._fb.group({
      otp1: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(1)],
      ],
      otp2: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(1)],
      ],
      otp3: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(1)],
      ],
      otp4: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(1)],
      ],
      otp5: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(1)],
      ],
      otp6: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(1)],
      ],
    });
  }

  verifyOtp() {
    if (this.otpVerificationForm.invalid) {
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    let data = {
      userId: this.userData.id,
      otp:
        this.otpVerificationForm.value.otp1 +
        this.otpVerificationForm.value.otp2 +
        this.otpVerificationForm.value.otp3 +
        this.otpVerificationForm.value.otp4 +
        this.otpVerificationForm.value.otp5 +
        this.otpVerificationForm.value.otp6,
    };
    this._AS.otpVerification(data).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.message === 'SSR mode') {
          return;
        }

        if (res.status) {
          if (
            requiresApproval(res.data)
          ) {
            this.router.navigateByUrl('/approval', { replaceUrl: true });
          } else {
            this._snackBar.open(
              res.message || 'Verification done successfully!',
              'close',
              {
                duration: 3000,
                panelClass: ['success-snackbar'],
              }
            );
            if (isPlatformBrowser(this.platformId)) {
              localStorage.setItem('Cirlced_User', JSON.stringify(res));
            }
            const redirectUrl = this._AS.getRedirectUrl() || '/search';
            this._AS.clearRedirectUrl();
            this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
            // this.router.navigate(['/search']);
          }
        } else {
          this.errorMessage =
            res.message || 'You need admin role to access this application.';
          this._snackBar.open(this.errorMessage, 'close', {
            duration: 3000,
            panelClass: ['error-snackbar'],
          });
        }
      },
      error: (error) => {
        this.isLoading = false;

        // Handle structured error response
        if (error.status === false) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage =
            'OTP Verification failed. Please check your credentials and try again.';
        }

        this._snackBar.open(this.errorMessage, 'close', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });

        // Use error handler for additional logging
        this.errorHandler.handleError(error, 'OTP Verification');
      },
    });
  }
  onInput(event: any, index: number) {
    let value = event.target.value;

    // Allow only digits
    value = value.replace(/\D/g, '');

    // 🔥 Autofill / paste case (full OTP)
    if (value.length > 1) {
      this.fillOtp(value);
      return;
    }

    // Set value
    this.otpVerificationForm.get(`otp${index + 1}`)?.setValue(value);

    // Move to next input
    if (value && index < 5) {
      this.otpInputs[index + 1].focus();
    }
  }

  fillOtp(otp: string) {
    const digits = otp.replace(/\D/g, '').slice(0, 6).split('');

    const patch: any = {};

    digits.forEach((d, i) => {
      patch[`otp${i + 1}`] = d;
    });

    this.otpVerificationForm.patchValue(patch);

    // Focus last input
    const lastIndex = digits.length - 1;
    if (this.otpInputs[lastIndex]) {
      this.otpInputs[lastIndex].focus();
    }

    // 🔥 Auto submit (optional)
    if (digits.length === 6) {
      this.verifyOtp();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        this.otpInputs[index - 1].focus();
      }
    }
  }
  onPaste(event: ClipboardEvent) {
    const pasteData = event.clipboardData?.getData('text') || '';
    this.fillOtp(pasteData);
    event.preventDefault();
  }

  startCountdown() {
    this.countdown = 20;
    this.showResend = false;
    this.interval = setInterval(() => {
      this.countdown--;
      if (this.countdown === 0) {
        this.showResend = true;
        clearInterval(this.interval);
      }
    }, 1000);
  }

  resendOtp() {
    this._AS.reSendOtp({ userId: this.userData.id }).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        // Handle SSR mode response
        if (res.message === 'SSR mode') {
          return;
        }

        if (res.status) {
          this._snackBar.open(
            res.message || 'OTP send successfully!',
            'close',
            {
              duration: 3000,
              panelClass: ['success-snackbar'],
            }
          );
        } else {
          this.errorMessage = res.message || 'Someting went wrong';
          this._snackBar.open(this.errorMessage, 'close', {
            duration: 3000,
            panelClass: ['error-snackbar'],
          });
        }
      },
      error: (error) => {
        this.isLoading = false;

        // Handle structured error response
        if (error.status === false) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage =
            'OTP send failed. Please check your credentials and try again.';
        }

        this._snackBar.open(this.errorMessage, 'close', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });

        // Use error handler for additional logging
        this.errorHandler.handleError(error, 'OTP Verification');
      },
    });
    // Restart countdown
    this.startCountdown();
  }
}
